import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {TaxonomyFlatNode} from '../taxonomy.component';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl} from '@angular/forms';
import {SubSink} from 'subsink';
import {MatTableDataSource} from '@angular/material/table';
import {Segment, SegmentModel} from '../../segment/segment.model';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {SegmentFacadeService} from '../../segment/segment-facade.service';
import {ofType} from '@ngrx/effects';
import {ActionsSubject} from '@ngrx/store';
import {flattenToSegments, segmentsAttached} from '../../segment/segments.state';
import {filter, map} from 'rxjs/operators';

export interface SegmentAttachToTaxonomyModalData {
  taxonomy: TaxonomyFlatNode;
}

@Component({
  selector: 'app-segment-attach-to-taxonomy-modal',
  template: `
      <div class="rowWrap step2">
          <div class="rowItem search">
              <h4>Taxonomy: {{data.taxonomy.name}}</h4>
              <h5>Search segments</h5>
              <mat-form-field>
                  <input [formControl]="segmentsFilterControl" matInput name="input-segment-search"
                         placeholder="Search a segment by name, ID, eID"
                         [autofocus]="true"/>
              </mat-form-field>
              <div class="tableWrapper">
                  <table mat-table [dataSource]="filteredSegmentsDataSource.filteredData|slice:0:5"
                         class="mat-elevation-z0"
                         *ngIf="showFilteredSegments()">
                      <ng-container matColumnDef="id">
                          <th mat-header-cell *matHeaderCellDef> Id</th>
                          <td mat-cell *matCellDef="let element"> {{element['id']}} </td>
                      </ng-container>
                      <ng-container matColumnDef="eid">
                          <th mat-header-cell *matHeaderCellDef> eId</th>
                          <td mat-cell *matCellDef="let element"> {{element['clientSideId']}} </td>
                      </ng-container>
                      <ng-container matColumnDef="name">
                          <th mat-header-cell *matHeaderCellDef> Name</th>
                          <td mat-cell *matCellDef="let element"> {{element['name']}} </td>
                      </ng-container>
                      <ng-container matColumnDef="add">
                          <th mat-header-cell *matHeaderCellDef></th>
                          <td mat-cell *matCellDef="let element" class="listAction">
                              <mat-icon matTooltip="Select Segment">add</mat-icon>
                          </td>
                      </ng-container>

                      <tr mat-header-row *matHeaderRowDef="['id', 'eid', 'name', 'add']"></tr>
                      <tr mat-row *matRowDef="let row; columns: ['id', 'eid', 'name', 'add'];"
                          (click)="addSegment(row)"></tr>
                  </table>

              </div>
              <div class="emptyTableMessage" *ngIf="!showFilteredSegments() && !showSpinner()">No segments found</div>
              <div class="emptyTableMessage" *ngIf="showSpinner()">
                  <mat-spinner></mat-spinner>
              </div>
          </div>
          <div class="rowItem select">
              <h5>Selected segments ({{selectedSegmentsDataSource.data.length}})</h5>
              <div class="tableWrapper selectedItems">
                  <table mat-table [dataSource]="selectedSegmentsDataSource" class="mat-elevation-z0"
                         *ngIf="showSelectedSegments()">
                      <ng-container matColumnDef="id">
                          <th mat-header-cell *matHeaderCellDef> Id</th>
                          <td mat-cell *matCellDef="let element"> {{element['id']}} </td>
                      </ng-container>
                      <ng-container matColumnDef="eid">
                          <th mat-header-cell *matHeaderCellDef> eId</th>
                          <td mat-cell *matCellDef="let element"> {{element['clientSideId']}} </td>
                      </ng-container>
                      <ng-container matColumnDef="name">
                          <th mat-header-cell *matHeaderCellDef> Name</th>
                          <td mat-cell *matCellDef="let element"> {{element['name']}} </td>
                      </ng-container>
                      <ng-container matColumnDef="remove">
                          <th mat-header-cell *matHeaderCellDef></th>
                          <td mat-cell *matCellDef="let element" class="listAction">
                              <mat-icon matTooltip="Remove Segment">remove</mat-icon>
                          </td>
                      </ng-container>

                      <tr mat-header-row *matHeaderRowDef="['id', 'eid', 'name', 'remove']"></tr>
                      <tr mat-row *matRowDef="let row; columns: ['id', 'eid', 'name', 'remove'];"
                          (click)="removeSegment(row)">
                      </tr>
                  </table>
              </div>
              <div class="emptyTableMessage" *ngIf="!showSelectedSegments()">No segments were selected</div>
          </div>
      </div>
      <div class="buttons-container">
          <button
                  color="primary"
                  class="back"
                  mat-button
                  (click)="closeDialog()">Close
          </button>
          <button
                  color="primary"
                  class="back"
                  mat-raised-button
                  (click)="attach()">Attach
          </button>
      </div>
  `,
  styleUrls: ['./segment-attach-to-taxonomy-modal.component.scss']
})
export class SegmentAttachToTaxonomyModalComponent implements OnInit, OnDestroy {
  segmentsFilterControl = new FormControl();
  filteredSegmentsDataSource = new MatTableDataSource<Segment>();
  selectedSegmentsDataSource = new MatTableDataSource<Segment>();
  private subSink = new SubSink();
  private segmentSearchTermSource = new BehaviorSubject('');
  private allSegmentsSource = new BehaviorSubject<Segment[]>([]);
  private selectedSegmentsSource = new BehaviorSubject<number[]>([]);
  selectedSegments = this.selectedSegmentsSource.asObservable();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SegmentAttachToTaxonomyModalData,
    public dialogRef: MatDialogRef<SegmentAttachToTaxonomyModalComponent>,
    private segmentFacade: SegmentFacadeService,
    private actions: ActionsSubject,
  ) {
  }

  private static sortBySegmentIdDescComparator(a: SegmentModel, b: SegmentModel) {
    return (a.id > b.id ? -1 : 1);
  }

  ngOnInit(): void {
    this.segmentFacade.loadSegmentsForTaxonomy(this.data.taxonomy.id);
    this.segmentFacade.fetchSegmentsForTaxonomyAttachment();

    this.subSink.sink = this.segmentsFilterControl.valueChanges.subscribe(searchTerm => {
      this.segmentSearchTermSource.next(searchTerm);
    });
    this.subSink.sink = this.actions.pipe(ofType(segmentsAttached))
      .subscribe(value => {
        this.dialogRef.close();
      });
    this.subSink.sink = this.segmentFacade.getAllSegmentsForTaxonomyAttachment()
      .subscribe({
        next: data => {
          this.allSegmentsSource.next(data);
          this.segmentSearchTermSource.next('');
        },
      });
    this.subSink.sink = this.segmentFacade.getSegmentsForTaxonomy(this.data.taxonomy.id)
      .pipe(filter(data => data && data.length >= 1),
        map(it => flattenToSegments(it)))
      .subscribe({
        next: data => {
          this.selectedSegmentsSource.next(data.map((it: Segment) => it.id));
          this.segmentSearchTermSource.next('');
        },
      });
    this.subSink.sink = combineLatest([
      this.selectedSegments,
      this.allSegmentsSource])
      .subscribe(([segmentIds, allSegments]) => {
        this.selectedSegmentsDataSource.data = segmentIds
          .map(id => allSegments.find((segment) => segment.id === id) as Segment)
          .filter(it => it !== undefined && it.id !== undefined)
          .sort(SegmentAttachToTaxonomyModalComponent.sortBySegmentIdDescComparator) as Segment[];
      });
    this.subSink.sink = combineLatest([
      this.allSegmentsSource,
      this.segmentSearchTermSource,
      this.selectedSegments])
      .subscribe(([allSegments, searchTerm, selectedAccountIds]) => {
        this.filteredSegmentsDataSource.data = allSegments.filter(segment => {
          // Filter already selected ones out
          if (selectedAccountIds.includes(segment.id)) {
            return false;
          }
          return searchTerm
            ? segment.id.toString().includes(searchTerm)
            || segment.name.toLowerCase().includes(searchTerm.toLowerCase())
            || (segment.clientSideId && segment.clientSideId.includes(searchTerm))
            : true;
        });
      });
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  showFilteredSegments() {
    return this.filteredSegmentsDataSource.data.length > 0;
  }

  showSpinner() {
    return this.filteredSegmentsDataSource.data.length === 0 && this.allSegmentsSource.value.length === 0;
  }

  showSelectedSegments() {
    return this.selectedSegmentsDataSource.data.length > 0;
  }

  addSegment(segment: SegmentModel) {
    const currEntries = this.selectedSegmentsSource.getValue();
    if (!currEntries.includes(segment.id)) {
      this.selectedSegmentsSource.next([...currEntries, segment.id]);
    }
  }

  removeSegment(segment: SegmentModel) {
    const newSelections = this.selectedSegmentsSource.getValue().filter((id) => id !== segment.id);
    this.selectedSegmentsSource.next(newSelections);
  }


  closeDialog() {
    this.dialogRef.close();
  }

  attach() {
    this.segmentFacade.attachToTaxonomy(this.data.taxonomy, this.selectedSegmentsSource.getValue());
  }
}
